import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  message,
  Dropdown,
  Menu,
  Switch,
} from "antd";
import { PlusOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Option } = Select;

const Staff = () => {
  const { permissions, isAdminTester } = usePermissions();
  const canView = isAdminTester && permissions?.includes(PERMISSIONS.MANAGE_STAFF);
  const canManage = canView;

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20, totalSize: 0, totalPage: 1 });

  const [roles, setRoles] = useState([]);
  const [canViewRoles, setCanViewRoles] = useState(true);
  const searchTimer = useRef(null);
  const rolesReqId = useRef(0);
  const staffReqId = useRef(0);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm] = Form.useForm();

  const [assignOpen, setAssignOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm] = Form.useForm();

  const [flagsOpen, setFlagsOpen] = useState(false);
  const [flagsSaving, setFlagsSaving] = useState(false);
  const [flagsTarget, setFlagsTarget] = useState(null);
  const [currentTestAccount, setCurrentTestAccount] = useState(null);
  const [flagsForm] = Form.useForm();

  const [suspending, setSuspending] = useState(false);

  const fetchRoles = useCallback(async () => {
    const id = ++rolesReqId.current;
    try {
      const res = await axios.get("/api/rbac/roles");
      if (id !== rolesReqId.current) return;
      setRoles(res.data.data || []);
      setCanViewRoles(true);
    } catch (error) {
      if (id !== rolesReqId.current) return;
      if (error.response?.status === 403) {
        setCanViewRoles(false);
        setRoles([]);
      } else {
        message.error(error.response?.data?.message || error.message);
      }
    }
  }, []);

  const fetchStaff = useCallback(async (page = 1) => {
    const id = ++staffReqId.current;
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get("/api/rbac/staff", { params });
      if (id !== staffReqId.current) return;
      setStaff(res.data.data || []);
      const ta = res.data.currentTestAccount;
      setCurrentTestAccount(ta && ta.email ? ta.email : null);
      const p = res.data.pagination || {};
      setPagination((prev) => ({
        ...prev,
        currentPage: p.currentPage || page,
        totalSize: p.totalSize || 0,
        totalPage: p.totalPage || 1,
      }));
    } catch (error) {
      if (id !== staffReqId.current) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      if (id === staffReqId.current) setLoading(false);
    }
  }, [search, roleFilter, statusFilter, pagination.limit]);

  useEffect(() => {
    if (canView) {
      fetchRoles();
    }
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchStaff(1);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search, roleFilter, statusFilter, canView, fetchStaff]);

  const openInvite = () => {
    inviteForm.setFieldsValue({ email: "", roleId: undefined });
    setInviteOpen(true);
  };

  const handleInvite = async () => {
    try {
      const values = await inviteForm.validateFields();
      setInviting(true);
      await axios.post("/api/rbac/staff/invite", {
        email: values.email,
        roleId: values.roleId || undefined,
      });
      message.success("Invite sent");
      setInviteOpen(false);
      fetchStaff(pagination.currentPage);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      setInviting(false);
    }
  };

  const openAssign = (record) => {
    setAssignTarget(record);
    assignForm.setFieldsValue({ roleId: record.roleId });
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      setAssigning(true);
      await axios.patch(`/api/rbac/staff/${assignTarget.id}/role`, { roleId: values.roleId });
      message.success("Role assigned");
      setAssignOpen(false);
      fetchStaff(pagination.currentPage);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleSuspend = async (record, suspended) => {
    setSuspending(true);
    try {
      await axios.post(`/api/rbac/staff/${record.id}/suspend`, { suspended });
      message.success(suspended ? "Suspended" : "Unsuspended");
      fetchStaff(pagination.currentPage);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setSuspending(false);
    }
  };

  const openFlags = (record) => {
    setFlagsTarget(record);
    flagsForm.setFieldsValue({
      isAdminTester: !!record.isAdminTester,
      isRbacTestAccount: !!record.isRbacTestAccount,
    });
    setFlagsOpen(true);
  };

  const handleFlags = async () => {
    try {
      const values = await flagsForm.validateFields();
      if (
        values.isRbacTestAccount &&
        !flagsTarget.isRbacTestAccount &&
        currentTestAccount &&
        currentTestAccount !== flagsTarget.email
      ) {
        await new Promise((resolve, reject) => {
          Modal.confirm({
            title: "Switch test account?",
            content: `This will unflag ${currentTestAccount} and make ${flagsTarget.email} the test account.`,
            onOk: resolve,
            onCancel: () => reject(new Error("cancelled")),
          });
        }).catch(() => {
          throw new Error("cancelled");
        });
      }
      setFlagsSaving(true);
      await axios.post(`/api/rbac/staff/${flagsTarget.id}/flags`, {
        isRbacTestAccount: values.isRbacTestAccount,
      });
      message.success("Flags updated");
      setFlagsOpen(false);
      fetchStaff(pagination.currentPage);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.message || error.message);
    } finally {
      setFlagsSaving(false);
    }
  };

  const columns = [
    {
      title: "Name / Email",
      dataIndex: "email",
      key: "email",
      render: (_, r) => (
        <div>
          <div>{r.name || "-"}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "roleCode",
      key: "roleCode",
      render: (v) => v || <Tag>none</Tag>,
    },
    {
      title: "Tester",
      dataIndex: "isAdminTester",
      key: "isAdminTester",
      render: (v) => (v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "RBAC Test Acct",
      dataIndex: "isRbacTestAccount",
      key: "isRbacTestAccount",
      render: (v) => (v ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Status",
      dataIndex: "suspendedAt",
      key: "suspendedAt",
      render: (v) => (v ? <Tag color="red">Suspended</Tag> : <Tag color="green">Active</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            {canViewRoles && <Menu.Item onClick={() => openAssign(record)}>Assign role</Menu.Item>}
            <Menu.Item onClick={() => openFlags(record)}>Edit flags</Menu.Item>
            {record.suspendedAt ? (
              <Menu.Item onClick={() => handleSuspend(record, false)}>Unsuspend</Menu.Item>
            ) : (
              <Menu.Item
                danger
                onClick={() =>
                  Modal.confirm({
                    title: "Suspend this user?",
                    content: "They will lose access until reactivated.",
                    onOk: () => handleSuspend(record, true),
                  })
                }
              >
                Suspend
              </Menu.Item>
            )}
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button size="small">
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Tag color="red">No access</Tag>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Staff Management" />
      <div style={{ padding: 16 }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search name/email"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
            allowClear
          />
          {canViewRoles && (
            <Select
              placeholder="Role"
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: 200 }}
              allowClear
            >
              {roles.map((r) => (
                <Option key={r.id} value={r.code || r.id}>
                  {r.name || r.code}
                </Option>
              ))}
            </Select>
          )}
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }}>
            <Option value="all">All</Option>
            <Option value="active">Active</Option>
            <Option value="suspended">Suspended</Option>
          </Select>
          {canManage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openInvite}>
              Invite staff
            </Button>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={staff}
          rowKey="id"
          loading={loading || suspending}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.limit,
            total: pagination.totalSize,
            onChange: (p) => fetchStaff(p),
          }}
        />

        <Modal
          title="Invite staff"
          visible={inviteOpen}
          onOk={handleInvite}
          onCancel={() => setInviteOpen(false)}
          confirmLoading={inviting}
          okText="Send invite"
        >
          <Form form={inviteForm} layout="vertical">
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, type: "email" },
                {
                  pattern: /^[^\s@]+@parceldaily\.com$/i,
                  message: "Only @parceldaily.com emails can be invited.",
                },
              ]}
            >
              <Input />
            </Form.Item>
            {canViewRoles && (
              <Form.Item name="roleId" label="Role" rules={[{ required: true, message: "Role is required" }]}>
                <Select placeholder="Select role">
                  {roles
                    .filter((r) => r.isSeeded)
                    .map((r) => (
                      <Option key={r.id} value={r.id}>
                        {r.name || r.code}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            )}
          </Form>
        </Modal>

        <Modal
          title={`Assign role — ${assignTarget?.email || ""}`}
          visible={assignOpen}
          onOk={handleAssign}
          onCancel={() => setAssignOpen(false)}
          confirmLoading={assigning}
        >
          <Form form={assignForm} layout="vertical">
            {canViewRoles ? (
              <Form.Item name="roleId" label="Role" rules={[{ required: true }]}>
                <Select>
                  {roles
                    .filter((r) => assignTarget?.isRbacTestAccount || r.isSeeded)
                    .map((r) => (
                      <Option key={r.id} value={r.id}>
                        {r.name || r.code}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            ) : (
              <p style={{ color: "#888" }}>Role assignment requires VIEW_ROLES permission.</p>
            )}
          </Form>
        </Modal>

        <Modal
          title={`Edit flags — ${flagsTarget?.email || ""}`}
          visible={flagsOpen}
          onOk={handleFlags}
          onCancel={() => setFlagsOpen(false)}
          confirmLoading={flagsSaving}
        >
          <Form form={flagsForm} layout="vertical">
            {currentTestAccount && (
              <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
                Current test account: {currentTestAccount}
              </p>
            )}
            <p style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
              isAdminTester: {flagsTarget?.isAdminTester ? "true" : "false"} (managed via database)
            </p>
            <Form.Item name="isRbacTestAccount" label="isRbacTestAccount" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Staff;
